import type {
  WorkspaceWithMemberCount,
  WorkspaceMemberWithUser,
  UpdateMemberRoleDto,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface WorkspaceState {
  workspaces: WorkspaceWithMemberCount[];
  currentWorkspace: WorkspaceWithMemberCount | null;
  members: WorkspaceMemberWithUser[];
  workspacesStatus: StateStatus;
  currentWorkspaceStatus: StateStatus;
  membersStatus: StateStatus;
  lastVisitWorkspaceId: number | null;
}

interface WorkspaceActions {
  fetchWorkspaces: () => Promise<void>;
  fetchWorkspace: (workspaceId: number) => Promise<void>;
  fetchLastVisit: () => Promise<void>;
  fetchMembers: (workspaceId: number) => Promise<void>;
  updateMemberRole: (
    workspaceId: number,
    userId: number,
    dto: UpdateMemberRoleDto,
  ) => Promise<void>;
  expelMember: (workspaceId: number, userId: number) => Promise<void>;
  updateWorkspaceName: (workspaceId: number, name: string) => Promise<void>;
  deleteWorkspace: (workspaceId: number) => Promise<void>;
  setCurrentWorkspace: (workspace: WorkspaceWithMemberCount | null) => void;
  reset: () => void;
}

export type WorkspaceStore = WorkspaceState & WorkspaceActions;

const initialState: WorkspaceState = {
  workspaces: [],
  currentWorkspace: null,
  members: [],
  workspacesStatus: "idle",
  currentWorkspaceStatus: "idle",
  membersStatus: "idle",
  lastVisitWorkspaceId: null,
};

export function createWorkspaceStore(deps: StoreDeps) {
  return createStore<WorkspaceStore>("WorkspaceStore", (set) => ({
    ...initialState,

    fetchWorkspaces: () =>
      withFetchStatus(
        (s) => set((state) => { state.workspacesStatus = s; }),
        deps.logger,
        async () => {
          const workspaces = await deps.api.workspace.list();
          set((state) => { state.workspaces = workspaces; });
        },
      ),

    fetchWorkspace: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.currentWorkspaceStatus = s; }),
        deps.logger,
        async () => {
          const workspace = await deps.api.workspace.get(workspaceId);
          set((state) => {
            state.currentWorkspace = workspace;
            state.lastVisitWorkspaceId = workspaceId;
          });
        },
      ),

    fetchLastVisit: async () => {
      try {
        const data = await deps.api.workspace.getLastVisit();
        set((state) => { state.lastVisitWorkspaceId = data.workspaceId; });
      } catch (error) {
        deps.logger.error(error);
      }
    },

    fetchMembers: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.membersStatus = s; }),
        deps.logger,
        async () => {
          const members = await deps.api.workspace.getMembers(workspaceId);
          set((state) => { state.members = members; });
        },
      ),

    updateMemberRole: async (workspaceId, userId, dto) => {
      const updatedMember = await deps.api.workspace.updateMemberRole(
        workspaceId,
        userId,
        dto,
      );
      set((state) => {
        const idx = state.members.findIndex((m) => m.userId === userId);
        if (idx !== -1) state.members[idx] = updatedMember;
      });
    },

    expelMember: async (workspaceId, userId) => {
      await deps.api.workspace.expelMember(workspaceId, userId);
      set((state) => {
        state.members = state.members.filter((m) => m.userId !== userId);
      });
    },

    updateWorkspaceName: async (workspaceId, name) => {
      const updated = await deps.api.workspace.update(workspaceId, { name });
      set((state) => {
        if (state.currentWorkspace?.id === workspaceId) {
          state.currentWorkspace.name = updated.name;
        }
        const idx = state.workspaces.findIndex((w) => w.id === workspaceId);
        if (idx !== -1) state.workspaces[idx].name = updated.name;
      });
    },

    deleteWorkspace: async (workspaceId) => {
      await deps.api.workspace.remove(workspaceId);
      set((state) => {
        state.workspaces = state.workspaces.filter(
          (w) => w.id !== workspaceId,
        );
        if (state.currentWorkspace?.id === workspaceId) {
          state.currentWorkspace = null;
        }
      });
    },

    setCurrentWorkspace: (workspace) =>
      set((state) => { state.currentWorkspace = workspace; }),

    reset: createReset(set as ImmerSet<WorkspaceState>, initialState),
  }));
}
